/**
 * Thin fetch wrapper over the @canvas-sync/api contract.
 *
 * Same-origin in production (CloudFront forwards /api/* to API Gateway),
 * dev-proxied in local mode (vite.config.ts).
 */

import type {
  CanvasSyncRunEvent,
  CanvasSyncUpload,
  CreateUploadRequest,
  CreateUploadResponse,
  GetRunResponse,
  GetSisBundleResponse,
  ListInputsResponse,
  MeResponse,
  ListSisBundlesResponse,
  ResubmitSisBundleRequest,
  ResubmitSisBundleResponse,
  RunSummary,
  SisBundleArtifactName,
  SisBundleInputFile,
  SisBundleStatus,
  SisBundleSubmissionMode,
  StartRunRequest,
  StartRunResponse,
  UploadSummary,
} from "@canvas-sync/shared";

const BASE = import.meta.env.VITE_API_URL ?? "/api";

// The EnvProvider (lib/env.tsx) updates this whenever the user flips the
// switcher, so every fetch carries `?env=prod|test`.
let currentEnv: "prod" | "test" = "test";
export function setApiCanvasEnv(env: "prod" | "test"): void {
  currentEnv = env;
}

// Module-level access-token getter, populated by AuthProvider in lib/auth.tsx.
// Done this way so every call site doesn't have to thread the auth context
// through. Returns null when no user is signed in; we still let the call
// hit API Gateway (it'll respond 401 and the UI can react), so this layer
// stays unaware of auth specifics.
let getAuthToken: () => string | null = () => null;
export function setAuthTokenGetter(fn: () => string | null): void {
  getAuthToken = fn;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const sep = path.includes("?") ? "&" : "?";
  const url = `${BASE}${path}${sep}env=${currentEnv}`;
  const token = getAuthToken();
  const res = await fetch(url, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const text = await res.text();
    // API errors are short JSON ({"error":"…"}), but an unexpected 5xx (or a
    // proxy/CloudFront error) can return a full HTML page or stack trace that
    // would otherwise dump verbatim into the on-screen error banner. Prefer
    // the structured `error` field and cap whatever's left.
    let detail = text;
    try {
      const parsed = JSON.parse(text) as { error?: string; detail?: string };
      detail = parsed.error ?? parsed.detail ?? text;
    } catch {
      /* not JSON — fall back to the raw (capped) text */
    }
    if (detail.length > 300) detail = `${detail.slice(0, 300)}…`;
    throw new Error(
      `${res.status} ${res.statusText}${detail ? `: ${detail}` : ""}`,
    );
  }
  // A 200 isn't necessarily the API: CloudFront's SPA fallback rewrites
  // unknown paths (e.g. a mangled ID that misses the /api/* behavior or a
  // 404 the error page swallows) to index.html with status 200. Parse
  // defensively so the operator sees a real message instead of
  // "Unexpected token '<' … is not valid JSON".
  const body = await res.text();
  try {
    return JSON.parse(body) as T;
  } catch {
    throw new Error(
      "The API returned an unexpected non-JSON response — the requested item may not exist.",
    );
  }
}

export interface UploadUrlResponse {
  url: string;
  fields: Record<string, string>;
  bucket: string;
  key: string;
  s3Uri: string;
}

export const api = {
  /** Caller's resolved app role (admin/user/none) — see lib/role.tsx. */
  getMe: () => request<MeResponse>("/me"),

  listRuns: (limit = 50) =>
    request<{ runs: RunSummary[]; nextCursor: string | null }>(
      `/runs?limit=${limit}`,
    ),

  getRun: (runId: string) => request<GetRunResponse>(`/runs/${runId}`),

  createRun: (body: StartRunRequest) =>
    request<StartRunResponse>("/runs", {
      method: "POST",
      // Send the active canvasEnv along with the request body so the API
      // doesn't have to choose between body+query — body wins.
      body: JSON.stringify({ canvasEnv: currentEnv, ...body }),
    }),

  rerunRun: (runId: string, body?: { submitToCanvas?: boolean }) =>
    request<StartRunResponse>(`/runs/${runId}/rerun`, {
      method: "POST",
      ...(body ? { body: JSON.stringify(body) } : {}),
    }),

  /**
   * Submit a run's already-generated fix CSV to Canvas as a new upload.
   * Mirrors `submitNewUpload`: 201 + uploadId on success, 502 + uploadId
   * when Canvas rejected the POST but a row was persisted in ERROR state.
   * The caller can navigate to `/uploads/{uploadId}` either way.
   */
  submitRunOutput: async (
    runId: string,
  ): Promise<{ response: CreateUploadResponse; canvasError?: string }> => {
    const url = `${BASE}/runs/${runId}/submit?env=${currentEnv}`;
    const token = getAuthToken();
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
    });
    const text = await res.text();
    let parsed: unknown;
    try {
      parsed = text ? JSON.parse(text) : {};
    } catch {
      throw new Error(
        `${res.status} ${res.statusText}: ${text.substring(0, 200)}`,
      );
    }
    const obj = parsed as Record<string, unknown> & {
      uploadId?: string;
      error?: string;
      detail?: string;
    };

    if (res.ok) {
      return { response: parsed as CreateUploadResponse };
    }
    if (obj.uploadId && typeof obj.uploadId === "string") {
      const canvasError =
        (typeof obj.detail === "string" && obj.detail) ||
        (typeof obj.error === "string" && obj.error) ||
        `Canvas returned ${res.status}`;
      return {
        response: {
          uploadId: obj.uploadId,
          status: "ERROR",
          canvasDomain: "",
          canvasEnv: currentEnv,
          canvasAccountId: "1",
          rowsSubmitted: 0,
        } as CreateUploadResponse,
        canvasError,
      };
    }
    const message =
      (typeof obj.error === "string" && obj.error) ||
      (typeof obj.detail === "string" && obj.detail) ||
      `${res.status} ${res.statusText}`;
    throw new Error(message);
  },

  getRunEvents: (runId: string) =>
    request<{ events: CanvasSyncRunEvent[]; nextCursor: string | null }>(
      `/runs/${runId}/events`,
    ),

  getRunOutputUrl: (runId: string) =>
    request<{ url: string; expiresAt: string }>(`/runs/${runId}/output-url`),

  getRunInputUrl: (runId: string) =>
    request<{ url: string; expiresAt: string; key: string; bucket: string }>(
      `/runs/${runId}/input-url`,
    ),

  getInputUploadUrl: (filename?: string) =>
    request<UploadUrlResponse>("/inputs/upload-url", {
      method: "POST",
      body: JSON.stringify({ filename }),
    }),

  listInputs: (limit = 50, cursor?: string) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (cursor) params.set("cursor", cursor);
    return request<ListInputsResponse>(`/inputs?${params.toString()}`);
  },

  getSettings: () =>
    request<{
      concurrency: number;
      maxRetries: number;
      retryDelayMs: number;
      recentEnrollmentHours: number;
      debug: boolean;
      defaultSubmitToCanvas?: boolean;
      sisBundleDryRun?: boolean;
      allowProdCanvasPost?: boolean;
      sisBundleSubmissionMode?: SisBundleSubmissionMode;
      expectedPrimaryTerm?: string;
      expectedSecondaryTerm?: string;
      updatedAt?: string;
      updatedBy?: string;
    }>("/settings"),

  putSettings: (body: {
    defaultSubmitToCanvas?: boolean;
    sisBundleDryRun?: boolean;
    allowProdCanvasPost?: boolean;
    sisBundleSubmissionMode?: SisBundleSubmissionMode;
    /** 4-digit term or "" to clear. Display/validation only. */
    expectedPrimaryTerm?: string;
    expectedSecondaryTerm?: string;
  }) =>
    request<{ updated: boolean; settings: Record<string, unknown> }>(
      "/settings",
      { method: "PUT", body: JSON.stringify(body) },
    ),

  testSubmit: (csv: string) =>
    request<{
      sisImportId: number;
      workflowState: string;
      rowsSubmitted: number;
      canvasDomain: string;
    }>("/test-submissions", {
      method: "POST",
      body: JSON.stringify({ csv }),
    }),

  testSubmitStatus: (sisImportId: number) =>
    request<{
      sisImportId: number;
      workflowState: string;
      terminal: boolean;
      succeeded: boolean;
      progress?: number;
      counts?: Record<string, number>;
      warnings: number;
      errors: number;
      createdAt?: string;
      startedAt?: string;
      endedAt?: string;
      canvasDomain: string;
    }>(`/test-submissions/${sisImportId}`),

  // ─── Uploads (Canvas SIS submissions) ────────────────────────────────
  listUploads: (limit = 50, runId?: string) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (runId) params.set("runId", runId);
    return request<{ uploads: UploadSummary[]; nextCursor: string | null }>(
      `/uploads?${params.toString()}`,
    );
  },

  getUpload: (uploadId: string) =>
    request<CanvasSyncUpload>(`/uploads/${uploadId}`),

  createUpload: (body: CreateUploadRequest) =>
    request<CreateUploadResponse>("/uploads", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /**
   * Submit a new upload, treating the (201, success) and (502 with uploadId,
   * row created in ERROR) paths the same way — the caller gets an uploadId
   * either way and can navigate to /uploads/{id} where the lifecycle UI
   * makes the failure clear. Only validation errors and other pre-row
   * failures throw.
   */
  submitNewUpload: async (
    body: CreateUploadRequest,
  ): Promise<{ response: CreateUploadResponse; canvasError?: string }> => {
    const url = `${BASE}/uploads?env=${currentEnv}`;
    const token = getAuthToken();
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    });
    const text = await res.text();
    let parsed: unknown;
    try {
      parsed = text ? JSON.parse(text) : {};
    } catch {
      throw new Error(
        `${res.status} ${res.statusText}: ${text.substring(0, 200)}`,
      );
    }
    const obj = parsed as Record<string, unknown> & {
      uploadId?: string;
      error?: string;
      detail?: string;
    };

    if (res.ok) {
      return { response: parsed as CreateUploadResponse };
    }

    // 502: Canvas rejected the POST but the row exists. Surface the
    // uploadId so the page can still navigate to the upload detail.
    if (obj.uploadId && typeof obj.uploadId === "string") {
      const canvasError =
        (typeof obj.detail === "string" && obj.detail) ||
        (typeof obj.error === "string" && obj.error) ||
        `Canvas returned ${res.status}`;
      return {
        response: {
          uploadId: obj.uploadId,
          status: "ERROR",
          canvasDomain: "",
          canvasEnv: currentEnv,
          canvasAccountId: "1",
          rowsSubmitted: 0,
        } as CreateUploadResponse,
        canvasError,
      };
    }

    // 400 / 4xx validation, or network blip without a row created.
    const message =
      (typeof obj.error === "string" && obj.error) ||
      (typeof obj.detail === "string" && obj.detail) ||
      `${res.status} ${res.statusText}`;
    throw new Error(message);
  },

  getUploadCsvUrl: (uploadId: string) =>
    request<{ url: string; expiresAt: string; sourceCsvS3Uri: string }>(
      `/uploads/${uploadId}/csv-url`,
    ),

  resubmitUpload: (uploadId: string) =>
    request<CreateUploadResponse>(`/uploads/${uploadId}/resubmit`, {
      method: "POST",
    }),

  // ─── SIS Bundles ──────────────────────────────────────────────────────
  listSisBundles: (
    opts: {
      status?: SisBundleStatus;
      limit?: number;
      cursor?: string;
    } = {},
  ) => {
    const params = new URLSearchParams();
    if (opts.limit !== undefined) params.set("limit", String(opts.limit));
    if (opts.status) params.set("status", opts.status);
    if (opts.cursor) params.set("cursor", opts.cursor);
    const query = params.toString();
    return request<ListSisBundlesResponse>(
      `/sis-bundles${query ? `?${query}` : ""}`,
    );
  },

  getSisBundle: (bundleId: string) =>
    // `#` in path params must be percent-encoded so it doesn't get parsed
    // as a fragment. encodeURIComponent → `%23`.
    request<GetSisBundleResponse>(
      `/sis-bundles/${encodeURIComponent(bundleId)}`,
    ),

  /**
   * Re-trigger a previous SIS bundle drop from its archived inputs.
   * Returns 202 + the predicted new bundleId; the bundler does the work
   * asynchronously via the S3 manifest trigger. The dashboard polls
   * GET /sis-bundles/{bundleId} for status.
   */
  resubmitSisBundle: (bundleId: string, body: ResubmitSisBundleRequest = {}) =>
    request<ResubmitSisBundleResponse>(
      `/sis-bundles/${encodeURIComponent(bundleId)}/resubmit`,
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    ),

  /**
   * Presigned GET URL for one archive artifact. `name` accepts the 7
   * named artifacts (manifest, fix, zip, result, validation,
   * canvas-response, contradiction-sample) OR one of the 5 PS SIS file
   * names (which resolve to inputs/{name}).
   *
   * `disposition`:
   *   "attachment" (default) — URL forces Save-As download
   *   "inline"               — URL renders in-browser (used by preview modal)
   *
   * The S3 OBJECT never expires (no lifecycle on sis-archive/); only the
   * presigned URL has a 5-min TTL. Re-call this to get a fresh URL.
   */
  getSisBundleArtifactUrl: (
    bundleId: string,
    name: SisBundleArtifactName | SisBundleInputFile,
    disposition: "inline" | "attachment" = "attachment",
  ) =>
    request<{
      url: string;
      expiresAt: string;
      s3Uri: string;
      downloadAs: string;
      disposition: "inline" | "attachment";
      bytes?: number;
      contentType?: string;
    }>(
      `/sis-bundles/${encodeURIComponent(bundleId)}/artifact/${encodeURIComponent(name)}?disposition=${disposition}`,
    ),
};

/**
 * Upload a File directly to S3 using a presigned POST. Returns the S3 URI.
 */
export async function uploadToS3(
  file: File,
  presigned: UploadUrlResponse,
): Promise<string> {
  const form = new FormData();
  for (const [k, v] of Object.entries(presigned.fields)) {
    form.append(k, v);
  }
  form.append("file", file);

  const resp = await fetch(presigned.url, { method: "POST", body: form });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(
      `S3 upload failed (${resp.status}): ${text.substring(0, 200)}`,
    );
  }
  return presigned.s3Uri;
}
